import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { Package, Droplets, UtensilsCrossed, Milk, Wheat, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
// import { useCartStore } from "@/stores/cart"; // Disabled — vitrine mode
import { formatFCFA } from "@cieda/utils";
// import type { Product } from "@cieda/types"; // Disabled — vitrine mode
// import { toast } from "sonner"; // Disabled — vitrine mode
import { SearchBar } from "@/components/search/SearchBar";
import { ff } from "@/lib/feature-flags";
import { ProductCardSkeleton } from "@/components/ui/Skeleton";
import { LazyImage } from "@/components/ui/LazyImage";
import { useRealtimeStock } from "@/hooks/useRealtimeStock";
// import { useOptimisticCart } from "@/hooks/useOptimisticCart"; // Disabled — vitrine mode

import { BRAND_IMAGE_MAP, DEFAULT_PRODUCT_IMAGE, FALLBACK_BRAND_FILTERS } from "@/lib/brand-data";

const DJAYMA_URL = "https://djayma.com";

const categories = [
  { id: "all", label: "Tous les produits", icon: Package },
  { id: "dairy", label: "Laitiers", icon: Milk },
  { id: "sauce", label: "Sauces", icon: UtensilsCrossed },
  { id: "food", label: "Conserves", icon: UtensilsCrossed },
  { id: "cleaning", label: "Entretien", icon: Droplets },
  { id: "rice", label: "Riz & Céréales", icon: Wheat },
  { id: "drink", label: "Boissons", icon: Package },
];


const CatalogSection = () => {
  const [active, setActive] = useState("all");
  const [activeBrand, setActiveBrand] = useState("all");
  // DISABLED — cart state (vitrine mode)
  // const [addedId, setAddedId] = useState<string | null>(null);
  // const { items: cartItems } = useCartStore();
  // const { addItem } = useOptimisticCart();
  const navigate = useNavigate();

  // Fetch brand list dynamically
  const { data: brandsData } = useQuery({
    queryKey: ["brandShowcases"],
    queryFn: api.cms.brandShowcases,
    staleTime: 5 * 60 * 1000,
  });
  const cmsBrandFilters = brandsData?.brands ?? [];
  const brandFilters = cmsBrandFilters.length > 0 ? cmsBrandFilters : [...FALLBACK_BRAND_FILTERS];

  // Fetch products from API
  const { data: apiData, isLoading } = useQuery({
    queryKey: ["catalog-products", active, activeBrand],
    queryFn: () => {
      const params: Record<string, string> = {};
      if (active !== "all") params.category = active;
      if (activeBrand !== "all") params.brand = activeBrand;
      return api.catalog.products(params);
    },
    staleTime: 60_000,
    retry: 1,
  });

  const products = apiData?.products ?? [];

  const productIds = products.map((p) => p.id);
  const liveStock = useRealtimeStock(productIds);

  // DISABLED — cart actions (vitrine mode)
  // const handleAddToCart = (product: Product) => {
  //   addItem(product, product.min_order_qty);
  //   setAddedId(product.id);
  //   toast.success(`${product.name} ajoute au panier`);
  //   setTimeout(() => setAddedId(null), 1500);
  // };
  // const isInCart = (productId: string) => cartItems.some((i) => i.product.id === productId);

  return (
    <section id="catalog" className="section-padding bg-muted/40 relative">
      <div className="container-max">
        <div className="text-center mb-14">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-3 py-1 rounded-md bg-secondary/10 text-secondary font-semibold text-xs uppercase tracking-[0.15em] mb-4">
              Catalogue B2B
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
              Nos <span className="text-gradient-gold">Produits</span>
            </h2>
            <p className="text-muted-foreground mt-3 max-w-md mx-auto">
              Decouvrez notre catalogue. Commandez sur{" "}
              <a href={DJAYMA_URL} target="_blank" rel="noopener noreferrer" className="text-secondary font-medium underline">djayma.com</a>.
            </p>
          </motion.div>
        </div>

        {/* AI search bar */}
        <SearchBar
          onSelectProduct={(id) => navigate(`/produit/${id}`)}
          className="mb-6 max-w-lg mx-auto"
        />

        {/* Brand filter chips — dynamic */}
        <div className="flex flex-wrap justify-center gap-2 mb-4">
          <button
            onClick={() => setActiveBrand("all")}
            className={`px-4 py-2 rounded-full text-xs font-medium transition-all duration-300 ${
              activeBrand === "all"
                ? "bg-secondary text-secondary-foreground shadow-md shadow-secondary/20"
                : "bg-card text-muted-foreground hover:text-foreground border border-border hover:border-foreground/20"
            }`}
          >
            Toutes les marques
          </button>
          {brandFilters.map((b) => {
            // Convert slug to DB enum format: "countre-dairy" → "countre_dairy"
            const brandKey = b.slug.replace(/-/g, "_");
            return (
              <button
                key={b.slug}
                onClick={() => setActiveBrand(brandKey)}
                className={`px-4 py-2 rounded-full text-xs font-medium transition-all duration-300 ${
                  activeBrand === brandKey
                    ? "bg-secondary text-secondary-foreground shadow-md shadow-secondary/20"
                    : "bg-card text-muted-foreground hover:text-foreground border border-border hover:border-foreground/20"
                }`}
              >
                {b.brand_name}
              </button>
            );
          })}
        </div>

        {/* Category filter tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActive(cat.id)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 sm:px-5 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium transition-all duration-300 ${
                active === cat.id
                  ? "bg-primary text-primary-foreground shadow-md shadow-primary/20"
                  : "bg-card text-muted-foreground hover:text-foreground border border-border hover:border-foreground/20"
              }`}
            >
              <cat.icon size={14} />
              {cat.label}
            </button>
          ))}
        </div>

        {/* Loading state */}
        {isLoading && (
          ff.skeletonScreens ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <ProductCardSkeleton key={i} />
              ))}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-card rounded-xl border border-border p-4 flex items-center gap-4 animate-pulse">
                  <div className="w-20 h-20 rounded-xl bg-muted" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-muted rounded w-3/4" />
                    <div className="h-3 bg-muted rounded w-1/2" />
                    <div className="h-4 bg-muted rounded w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          )
        )}

        {/* Product grid */}
        {!isLoading && products.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <AnimatePresence mode="popLayout">
              {products.map((product) => (
                <motion.div
                  key={product.id}
                  layout
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.3 }}
                  className="bg-card rounded-xl border border-border p-4 flex items-center gap-4 hover:border-secondary/30 transition-all duration-300 group cursor-pointer relative overflow-hidden"
                  style={{ boxShadow: "var(--card-shadow)" }}
                >
                  <div className="w-20 h-20 rounded-xl bg-muted/60 flex items-center justify-center flex-shrink-0 group-hover:bg-secondary/10 transition-colors overflow-hidden">
                    <LazyImage
                      src={product.image_url || BRAND_IMAGE_MAP[product.brand] || DEFAULT_PRODUCT_IMAGE}
                      alt={product.name}
                      className="w-full h-full object-cover rounded-xl transition-transform duration-500 group-hover:scale-110"
                      skeletonClassName="w-20 h-20 rounded-xl"
                      testId={`product-img-${product.id}`}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-foreground text-sm truncate">{product.name}</h4>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-xs text-muted-foreground capitalize">{product.brand?.replace("_", " ")}</span>
                      <span className="w-1 h-1 rounded-full bg-border" />
                      <span className="text-xs text-muted-foreground">{product.unit}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-secondary font-bold text-sm">{formatFCFA(product.price_fcfa)}</span>
                      {(() => {
                        const stock = ff.realtimeUpdates ? (liveStock.get(product.id) ?? product.stock) : product.stock;
                        return (
                          <>
                            {stock < 20 && stock > 0 && (
                              <span className="text-xs text-amber-600">{ff.realtimeUpdates ? `${stock} restants` : "Stock limité"}</span>
                            )}
                            {stock === 0 && (
                              <span className="text-xs text-destructive">Rupture</span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  {/* Voir sur Djayma — replaces add-to-cart (vitrine mode) */}
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300">
                    <a
                      href={`${DJAYMA_URL}/produit/${product.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold shadow-md hover:bg-gold-light transition-colors whitespace-nowrap"
                      aria-label={`Voir ${product.name} sur Djayma`}
                    >
                      Voir sur Djayma
                      <ExternalLink size={12} />
                    </a>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && products.length === 0 && (
          <div className="text-center py-16">
            <Package size={48} className="mx-auto text-muted-foreground/40 mb-4" />
            <p className="text-muted-foreground text-lg">
              Aucun produit disponible pour le moment.
            </p>
          </div>
        )}

        {/* CTA — Djayma boutique link (vitrine mode) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <a
            href={DJAYMA_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2.5 px-8 py-4 rounded-full bg-secondary text-secondary-foreground font-semibold text-base hover:bg-gold-light transition-all duration-300 shadow-lg shadow-secondary/15 shine"
          >
            Commander sur Djayma
            <ExternalLink size={18} />
          </a>
          <p className="text-xs text-muted-foreground mt-3">Passez vos commandes sur notre boutique en ligne djayma.com</p>
        </motion.div>

        {/* DISABLED — original cart CTA (vitrine mode)
        {cartItems.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link
              to="/panier"
              className="inline-flex items-center gap-2.5 px-8 py-4 rounded-full bg-primary text-primary-foreground font-semibold text-base hover:bg-navy-mid transition-all duration-300 shadow-lg shadow-primary/15 shine"
            >
              <ShoppingCart size={18} />
              Voir le panier ({cartItems.reduce((s, i) => s + i.quantity, 0)} articles)
            </Link>
            <p className="text-xs text-muted-foreground mt-3">Livraison disponible dans la region de Dakar</p>
          </motion.div>
        )}
        */}
      </div>
    </section>
  );
};

export default CatalogSection;
